import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import type { IonInput } from "@ionic/core/components/ion-input";
import { FinalFormController, zodValidator } from "../controllers/final-form.controller";
import { ClientResponseError } from "pocketbase";
import { consume } from "@lit/context";
import { authContext, type AuthContext } from "../context/auth.context";
import { routerContext, type RouterContext } from "../context/router.context";
import { Page } from "../components/component";
import { localized, msg } from "@lit/localize";
import { userCreateSchema, type UserCreate } from "../repository/user/user";
import { WithInstallPrompt } from "../mixins/installPrompt";

@customElement("page-login")
@localized()
export class PageLogin extends WithInstallPrompt(Page) {
  @property({ type: Boolean }) private _showPassword = false;

  @consume({ context: routerContext }) router!: RouterContext;
  @consume({ context: authContext }) auth!: AuthContext;

  #controller = new FinalFormController<UserCreate>(this, {
    validate: zodValidator(userCreateSchema()),
    onSubmit: async (values) => {
      try {
        const user = await this.auth.loginWithEmail(values.email, values.password);

        if (user?.isProfileComplete) {
          window.dispatchEvent(new CustomEvent("locale-changed", { detail: user.language }));
          this.router.push("/home", "root");
        } else {
          this.router.push("/register/create", "forward");
        }
      } catch (error) {
        if (error instanceof ClientResponseError) {
          if (error.status === 400) {
            this.#controller.setError("email", msg("Email ou password inválida"));
          } else {
            this.#controller.setError("email", msg("Erro ao fazer login, tente novamente mais tarde"));
          }
        }
      }
    },
  });

  render() {
    const { form, register, renderError } = this.#controller;
    const formState = form.getState();

    return html`
      <ion-content fullscreen>
        <header class="mt-4">
          <div class="flex justify-between items-center">
            <h3 class="font-semibold">${msg("Entrar")}</h3>
            <div class="flex items-center">
              <ion-router-link href="/register" router-direction="back">
                <ion-button
                  fill="clear"
                  style="--padding-end: 0; --padding-top: 0; --padding-bottom: 0;"
                  class="font-medium underline underline-offset-2"
                  >${msg("Criar conta")}
                  <span class="mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M10 8L14 12L10 16"
                        stroke="black"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </span>
                </ion-button>
              </ion-router-link>
            </div>
          </div>
        </header>

        <div class="mt-4">
          <form
            id="form-login"
            @submit=${(e: Event) => {
              e.preventDefault();
              form.submit();
            }}>
            <ion-list lines="none" class="space-y-4 flex flex-col">
              <div>
                <ion-item>
                  <ion-input id="email" type="email" placeholder="Email" ${register("email")}></ion-input>
                </ion-item>
                ${renderError("email")}
              </div>

              <div>
                <ion-item>
                  <ion-input id="password" type="password" placeholder="Password" ${register("password")}></ion-input>
                  <ion-button
                    slot="end"
                    style="--padding-end: 0;"
                    fill="clear"
                    id="passwordBtn"
                    @click=${this._onClickPasswordBtn}>
                    <span ?hidden=${this._showPassword}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M3.5868 13.7788C5.36623 15.5478 8.46953 17.9999 12.0002 17.9999C15.5308 17.9999 18.6335 15.5478 20.413 13.7788C20.8823 13.3123 21.1177 13.0782 21.2671 12.6201C21.3738 12.2933 21.3738 11.7067 21.2671 11.3799C21.1177 10.9218 20.8823 10.6877 20.413 10.2211C18.6335 8.45208 15.5308 6 12.0002 6C8.46953 6 5.36623 8.45208 3.5868 10.2211C3.11714 10.688 2.88229 10.9216 2.7328 11.3799C2.62618 11.7067 2.62618 12.2933 2.7328 12.6201C2.88229 13.0784 3.11714 13.3119 3.5868 13.7788Z"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                        <path
                          d="M10 12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12Z"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                      </svg>
                    </span>
                    <span ?hidden=${!this._showPassword}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M3.99989 4L19.9999 20M16.4999 16.7559C15.1473 17.4845 13.6185 17.9999 11.9999 17.9999C8.46924 17.9999 5.36624 15.5478 3.5868 13.7788C3.1171 13.3119 2.88229 13.0784 2.7328 12.6201C2.62619 12.2933 2.62616 11.7066 2.7328 11.3797C2.88233 10.9215 3.11763 10.6875 3.58827 10.2197C4.48515 9.32821 5.71801 8.26359 7.17219 7.42676M19.4999 14.6335C19.8329 14.3405 20.138 14.0523 20.4117 13.7803L20.4146 13.7772C20.8832 13.3114 21.1182 13.0779 21.2674 12.6206C21.374 12.2938 21.3738 11.7068 21.2672 11.38C21.1178 10.9219 20.8827 10.6877 20.4133 10.2211C18.6338 8.45208 15.5305 6 11.9999 6C11.6624 6 11.3288 6.02241 10.9999 6.06448M13.3228 13.5C12.9702 13.8112 12.5071 14 11.9999 14C10.8953 14 9.99989 13.1046 9.99989 12C9.99989 11.4605 10.2135 10.9711 10.5608 10.6113"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                      </svg>
                    </span>
                  </ion-button>
                </ion-item>
                ${renderError("password")}
              </div>
            </ion-list>
          </form>
        </div>
      </ion-content>
      <ion-footer>
        <ion-toolbar>
          <ion-button
            form="form-login"
            type="submit"
            color="primary"
            expand="block"
            shape="round"
            class="font-semibold"
            ?disabled=${formState.submitting}
            >${msg("Entrar")}</ion-button
          >
          <div>
            <ion-router-link href="/recover-password" router-direction="forward">
              <ion-button fill="clear" color="primary" expand="block" shape="round" class="font-semibold"
                >${msg("Esqueceste a tua password?")}</ion-button
              >
            </ion-router-link>
          </div>
        </ion-toolbar>
      </ion-footer>
    `;
  }

  private _onClickPasswordBtn() {
    const passwordInput = document.getElementById("password") as IonInput;

    if (this._showPassword) {
      passwordInput.type = "password";
    } else {
      passwordInput.type = "text";
    }

    this._showPassword = !this._showPassword;
  }
}
